import { useContext, useRef, useState } from 'react';
import { vaultAccess } from 'helpers/sharing';
import { useParams } from 'react-router-dom';
import { CrossIcon, SearchIcon, WrittenLogo } from 'assets/icons';
import {
  AlbumAddContentTypes,
  Breakpoints,
  NotificationType,
  VaultNavigationTabs,
} from 'common/enums';
import { TopnavDropdownMenu } from 'common/topnav-dropdown-menu';
import { SearchBarChip, VaultSearchBar } from 'common/vault-search-bar';
import { excludeDefaultAlbum } from 'helpers/utils';
import type { Breadcrumb } from 'helpers/vault-breadcrumbs';
import { withOrigin } from 'helpers/vault-breadcrumbs';
import { useBreadcrumbOrigin } from 'hooks/use-breadcrumb-origin';
import { useMediaQuery } from 'hooks/use-media-query';
import { appActions, AppContext } from 'context';
import {
  VaultDetailContext,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { trackCustomEvent } from 'helpers/analytics';
import { NotificationObject } from 'models/notificationObject';
import { UserController } from 'networking/controllers/user-controller';
import { captureEvent } from 'posthog';
import { useCreateAlbum } from 'pages/vault-detail/hooks/use-create-album';
import { goToPage, RouteName } from 'routes';
import { NewEntryModal } from 'common/new-entry-modal';
import { UploadModal } from 'common/upload-modal';
import { SuggestedPromptsModal } from 'pages/single-album/components/suggested-prompts-modal';
import { VaultBottomNav } from '../vault-bottom-nav';
import { VideoCallWaitingRoom } from '../video-call-waiting-room';
import { NewMenu } from './components/new-menu';
import { NewAlbumModal } from './components/new-album-modal';
import { ViewButton } from './components/view-button';
import styles from './vault-top-bar.module.scss';

export type { Breadcrumb };

interface VaultTopBarProps {
  vault: VaultType;
  // Shown as a preview chip inside the search bar (e.g. the open album).
  searchChip?: SearchBarChip;
  // The trail back to the vault dashboard; see helpers/vault-breadcrumbs. The
  // first crumb is always the dashboard — leaving the vault is done through
  // the logo or the vault switcher, never from here.
  breadcrumb: Breadcrumb[];
  // The album whose page this bar sits on: "New → Upload" adds to it.
  uploadAlbum?: { id: number; name: string; thumbnailUrl?: string | null };
  // The vault's tabs. On phones they become a bottom bar with New's "+", as
  // in the mobile app.
  bottomNav?: {
    selectedTab: VaultNavigationTabs;
    onSelectTab: (tab: VaultNavigationTabs) => void;
  };
}

const goToVaults = () => goToPage(RouteName.Vaults);

export const VaultTopBar = ({
  vault,
  searchChip,
  breadcrumb,
  uploadAlbum,
  bottomNav,
}: VaultTopBarProps) => {
  const { dispatch, state: appState } = useContext(AppContext);
  // What this person's role lets them add (owner and editors: everything).
  const access = vaultAccess(appState.data.user, vault);
  const canAdd =
    access.fullAccess ||
    ['contributor', 'limited_contributor'].includes(access.role || '');
  const { dispatch: dispatchVaultDetail } = useContext(VaultDetailContext);
  const createAlbum = useCreateAlbum();
  // Address the vault by its route param (as the rest of the app does), not the
  // fetched vault object's id — after the album→vault rename those can differ.
  // `albumId` is present when we're inside an album view (used as the default
  // target album for a new entry).
  const { id: urlVaultId, albumId: urlAlbumId } = useParams<{
    id?: string;
    albumId?: string;
  }>();
  const vaultId = Number(urlVaultId) || vault.id;
  const currentAlbumId = urlAlbumId ? Number(urlAlbumId) : undefined;
  const breadcrumbOrigin = useBreadcrumbOrigin();
  // The trail's first crumb is the vault's dashboard. The bar shows only the
  // vault's name, which leads back to it (plain text on the dashboard itself).
  const goToDashboardCrumb = breadcrumb[0]?.onClick;

  // "View" opens the player for what's on screen: the album when inside one,
  // otherwise every memory in the vault. The origin is forwarded so the player
  // keeps this screen's breadcrumb trail.
  const handleView = () => {
    const origin = withOrigin(breadcrumbOrigin);
    if (currentAlbumId) {
      goToPage(
        RouteName.AlbumViewScreen,
        { id: vaultId, albumId: currentAlbumId },
        {},
        origin,
      );
    } else {
      goToPage(RouteName.MemoriesViewScreen, { id: vaultId }, {}, origin);
    }
  };

  // The user's albums in this vault, offered as the target for a new entry.
  const albumOptions = excludeDefaultAlbum([
    ...(vault.albums ?? []),
    ...(vault.unansweredAlbums ?? []),
  ]).map((album) => ({ id: album.id, name: album.name }));
  const handleSearch = (query: string) =>
    dispatchVaultDetail({
      type: vaultDetailActions.SET_SEARCH_QUERY,
      searchQuery: query,
    });

  // Where the search bar doesn't fit, a search button opens it over the bar
  // (the mobile app's search header); closing it clears the search.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const openSearch = () => {
    setSearchOpen(true);
    requestAnimationFrame(() =>
      searchRef.current?.querySelector('input')?.focus(),
    );
  };
  const closeSearch = () => {
    setSearchOpen(false);
    handleSearch('');
  };

  // On phones New is the bottom bar's "+".
  const isPhone = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const phoneNav = !!bottomNav && isPhone;

  // "New Album" → a small modal that asks for the album name.
  const [showNewAlbumModal, setShowNewAlbumModal] = useState(false);
  // "New Memory" (note/audio/video/upload) → opens the matching creator modal,
  // where the user picks which existing album to add the entry to.
  const [newEntryMode, setNewEntryMode] = useState<AlbumAddContentTypes | null>(
    null,
  );

  // Create Album: name it, create it, then open its page.
  const handleCreateNamedAlbum = async (name: string) => {
    setShowNewAlbumModal(false);
    const album = await createAlbum(vaultId, name);
    if (!album) return;
    goToPage(RouteName.AlbumScreen, { id: vaultId, albumId: album.id });
  };

  // "Video Call" → the same waiting room the Planner's "Start video call" opens.
  const [showVideoCall, setShowVideoCall] = useState(false);
  // "New Prompt" → the Planner's "Add new prompt" picker, with an album select.
  const [showPrompts, setShowPrompts] = useState(false);

  // "Upload" → pick files or folders, then the import review.
  const [showUploadModal, setShowUploadModal] = useState(false);

  const logOutUser = async () => {
    captureEvent('logout');
    trackCustomEvent('logOut');
    try {
      await UserController.signOutUser();
      dispatch({ type: appActions.SESSION_EXPIRED });
      goToPage(RouteName.SignIn);
    } catch (e) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'We couldn’t log you out. Please try again.',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const newMenuProps = canAdd
    ? {
        onUpload: () => setShowUploadModal(true),
        onCreateNote: () => setNewEntryMode(AlbumAddContentTypes.write),
        onCreateAudio: () => setNewEntryMode(AlbumAddContentTypes.audio),
        onCreateVideo: () => setNewEntryMode(AlbumAddContentTypes.video),
        onCreateVideoCall: access.fullAccess
          ? () => setShowVideoCall(true)
          : undefined,
        // Prompts and albums: not for limited contributors.
        onNewPrompt: access.canCreateAlbum
          ? () => setShowPrompts(true)
          : undefined,
        onCreateAlbum: () => setShowNewAlbumModal(true),
        showAlbumSection: access.canCreateAlbum,
      }
    : undefined;

  return (
    <nav className={styles.topBar}>
      <div className={styles.leftSection}>
        <button type="button" onClick={goToVaults} className={styles.logoBlock}>
          <span className={styles.logo}>
            <WrittenLogo />
          </span>
          {vault.owner?.companyName && (
            <span className={styles.poweredBy}>
              {'Powered by '}
              <span className={styles.poweredByName}>
                {vault.owner.companyName}
              </span>
            </span>
          )}
        </button>
        {vault.title && (
          <>
            <span className={styles.vaultDivider} aria-hidden />
            {goToDashboardCrumb ? (
              <button
                type="button"
                className={styles.vaultName}
                onClick={goToDashboardCrumb}
                title={vault.title}
              >
                <span className={styles.vaultNameText}>{vault.title}</span>
              </button>
            ) : (
              <span className={styles.vaultName} title={vault.title}>
                <span className={styles.vaultNameText}>{vault.title}</span>
              </span>
            )}
          </>
        )}
      </div>
      <div className={styles.centerSection}>
        <VaultSearchBar
          placeholder={searchChip ? 'Search' : 'Search this Vault'}
          onSearch={handleSearch}
          chip={searchChip}
        />
      </div>
      <div className={styles.rightSection}>
        <ViewButton onClick={handleView} />
        <button
          type="button"
          className={styles.searchButton}
          onClick={openSearch}
          aria-label="Search"
        >
          <SearchIcon />
        </button>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {!phoneNav && newMenuProps && <NewMenu {...newMenuProps} />}
        <TopnavDropdownMenu onLogout={logOutUser} vault={vault} />
      </div>

      {searchOpen && (
        <div className={styles.searchOverlay} ref={searchRef}>
          <VaultSearchBar
            placeholder={searchChip ? 'Search' : 'Search this Vault'}
            onSearch={handleSearch}
            chip={searchChip}
          />
          <button
            type="button"
            className={styles.searchClose}
            onClick={closeSearch}
            aria-label="Close search"
          >
            <CrossIcon />
          </button>
        </div>
      )}

      {phoneNav && bottomNav && (
        <VaultBottomNav
          selectedTab={bottomNav.selectedTab}
          onSelectTab={bottomNav.onSelectTab}
          newMenu={newMenuProps}
        />
      )}

      {showNewAlbumModal && (
        <NewAlbumModal
          onSubmit={handleCreateNamedAlbum}
          onClose={() => setShowNewAlbumModal(false)}
        />
      )}

      {showUploadModal && (
        <UploadModal
          vaultId={vaultId}
          album={uploadAlbum}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {showVideoCall && (
        <VideoCallWaitingRoom
          vault={vault}
          onClose={() => setShowVideoCall(false)}
        />
      )}

      {showPrompts && (
        <SuggestedPromptsModal
          vaultId={vaultId}
          albums={albumOptions}
          albumId={
            albumOptions.some((a) => a.id === currentAlbumId)
              ? currentAlbumId
              : undefined
          }
          onClose={() => setShowPrompts(false)}
          onAdded={() =>
            dispatchVaultDetail({
              type: vaultDetailActions.REQUEST_ALBUMS_REFRESH,
            })
          }
        />
      )}

      {newEntryMode && (
        <NewEntryModal
          mode={newEntryMode}
          vault={vault}
          albums={albumOptions}
          defaultAlbumId={currentAlbumId}
          onClose={() => setNewEntryMode(null)}
        />
      )}
    </nav>
  );
};
